import { FC } from 'react';
import Modal from './FiligraneBaseModal';
import './FAQModal.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  questionIndex: number;
}

interface FaqStep {
  title: string;
  text: string;
  warning?: boolean;
}

interface FaqNote {
  type: 'warning' | 'tip';
  text: string;
}

interface FaqData {
  question: string;
  intro: string;
  steps: FaqStep[];
  note?: FaqNote;
}

const FAQ_DATA: FaqData[] = [
  {
    question: 'Comment fonctionne mon filigrane ?',
    intro:
      "Le filigrane moderne ne se contente pas de masquer un document, il en restreint techniquement et juridiquement l'usage. Pour vos documents sensibles (pièces d'identité, justificatifs), son efficacité repose sur trois piliers :",
    steps: [
      {
        title: 'Le ciblage contextuel',
        text: "En inscrivant directement sur le document le contexte et le nom du destinataire (par exemple : \"Document fourni exclusivement à l'Agence X pour un dossier de location\"), vous rendez le fichier inutilisable pour toute autre démarche en cas de fuite.",
      },
      {
        title: 'Une barrière anti-IA',
        text: "Les technologies de pointe, comme le filigrane ID Protect, appliquent des marquages sécurisés complexes. Ces protections sont conçues pour résister aux outils de retouche basés sur l'intelligence artificielle, qui échouent à les effacer.",
      },
      {
        title: 'La limite majeure',
        text: "Les banques et les organismes de crédit exigeront toujours vos documents sans aucun filigrane. Ces institutions utilisent des outils automatisés pour vérifier la validité des pièces d'identité, et l'ajout d'un marquage fausse leurs algorithmes de contrôle.",
        warning: true,
      },
    ],
  },
  {
    question: 'Pour quel usage dois-je utiliser le filigrane ?',
    intro:
      'Le filigrane ID Protect est votre bouclier idéal pour sécuriser les échanges de particulier à particulier.',
    steps: [
      {
        title: "Quand l'utiliser",
        text: "Activez-le dès que vous transmettez un document à un inconnu (comme en répondant à une annonce immobilière sur internet) ou à de petites associations qui ne disposent pas d'un service de cybersécurité. Sa technologie avancée empêche les outils d'intelligence artificielle de supprimer le marquage.",
      },
      {
        title: "Quand l'éviter",
        text: "Ne l'utilisez jamais pour vos démarches auprès des administrations, des banques, des organismes de crédit ou des grands professionnels de la location. Votre document sera systématiquement refusé.",
        warning: true,
      },
    ],
    note: {
      type: 'warning',
      text: "Ces institutions utilisent des logiciels automatisés pour vérifier l'authenticité de vos pièces d'identité. Le filigrane perturbe ces outils et déclenche une fausse alerte de falsification. N'étant plus en mesure de valider votre document, ces organismes exigeront toujours un original sans filigrane.",
    },
  },
  {
    question: 'Quels types de documents peuvent être filigranés ?',
    intro: 'Tous vos fichiers du quotidien peuvent recevoir un marquage par filigrane.',
    steps: [
      {
        title: 'Les documents PDF',
        text: "C'est le format idéal pour vos pièces d'identité, contrats ou justificatifs. Une fois le PDF \"aplati\" (lorsque le texte et le filigrane sont fusionnés en une seule couche), le marquage fait corps avec le document, ce qui le rend très difficile à dissocier.",
      },
      {
        title: 'Les fichiers Word',
        text: "Bien qu'il soit techniquement possible d'ajouter un filigrane sur Word, ce format reste modifiable par nature. N'importe quel destinataire peut ouvrir le fichier et supprimer votre filigrane en deux clics. Convertissez toujours vos documents Word en PDF avant de les envoyer.",
        warning: true,
      },
      {
        title: 'Les images (JPEG, PNG)',
        text: "C'est la norme pour les créateurs de contenus, graphistes ou photographes qui partagent leurs visuels sur le web pour éviter le vol d'images.",
      },
    ],
  },
];

const FAQModal: FC<Props> = ({ isOpen, onClose, questionIndex }) => {
  const faq = FAQ_DATA[questionIndex] ?? FAQ_DATA[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Questions fréquentes" size="md">
      <div className="faq-modal">

        <h3 className="faq-modal__question">{faq.question}</h3>

        <p className="faq-modal__intro">{faq.intro}</p>

        {faq.steps.length > 0 && (
          <div className="faq-modal__steps">
            {faq.steps.map((step, i) => (
              <div key={i} className={`faq-modal__step${step.warning ? ' faq-modal__step--warning' : ''}`}>
                <strong className="faq-modal__step-title">{step.title}</strong>
                <span className="faq-modal__step-text">{step.text}</span>
              </div>
            ))}
          </div>
        )}

        {faq.note && (
          <div className={`faq-modal__note faq-modal__note--${faq.note.type}`}>
            <p>{faq.note.text}</p>
          </div>
        )}

      </div>
    </Modal>
  );
};

export default FAQModal;
